import {
  Typography,
  Divider,
  Switch,
  Stack,
  Select,
  Box,
  MenuItem,
  ListItemText,
} from '@mui/material';
import { Severity, type ControlRules } from '@repo/api-gw-sdk';
import classNames from 'classnames';
import React from 'react';

import { ConditionEditor } from '@/components/conditions/ConditionEditor';
import { controlRules } from '@/components/controls/ControlRules';
import { ControlIndicator } from '@/components/controls/controlIndicator';
import { supportedControlProperties } from '@/components/controls/supportedControlProperties';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { RenameEntityName } from '@/components/wizard/renameEntityName';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { ControlsSeverity } from '@/data/inventory/data';

import type { ControlEditState } from './ControlCreationFlow';

export const ControlConditionEditor = (props: StepProps<ControlEditState>) => {
  const {
    wizardState: { condition, rules },
  } = props;
  const { tagsFiltersV2 } = useFeatureFlags();
  const updateState = (state: Partial<ControlEditState>) =>
    props.setWizardState({ ...props.wizardState, ...state });
  const updateRule = (newRules: ControlRules) =>
    updateState({ rules: { ...rules, ...newRules } });
  const updateSeverity = (severity: Severity) => updateState({ severity });

  return (
    <StepContainer
      sx={{
        minHeight: '100%',
        padding: 0,
        '&:last-child': { paddingBottom: 0 },
        display: 'flex',
      }}
      hideStepper={true}
      onBackClick={props.back}
      backButtonText='Cancel'
      canGoNext={() => true}
      onNextClick={() => props.finish()}
      nextButtonText={props.wizardState.id ? 'Save Changes' : 'Create'}
      navigationComponent={
        <RenameEntityName
          label='Control name'
          name={props.wizardState.name}
          onChange={(name) =>
            props.setWizardState((state) => ({ ...state, name }))
          }
        />
      }
    >
      <Stack className='w-full'>
        <Typography
          fontWeight={300}
          className='ml-[100px] mt-[24px] mb-[40px] w-[700px]'
        >{`Select at least one rule for this control to monitor and define which resources should be backed up accordingly. Eon will alert you if any of the selected rules are violated by your backup policies.`}</Typography>
        <Divider />
        <Stack direction='row' flexGrow={1}>
          <div className='w-1/2'>
            <Typography
              variant='body1'
              sx={{ padding: '60px 0 20px 100px' }}
              fontWeight={600}
            >
              What rules should your backup follow?
            </Typography>
            <Stack divider={<Divider />}>
              {controlRules.map(
                ({ enabled, Editor, onToggle, label, icon }, i) => {
                  const isEnabled = enabled(rules);
                  return (
                    <Stack
                      key={i}
                      direction='row'
                      alignItems='center'
                      className='px-[100px] py-[24px]'
                      flexWrap={'wrap'}
                    >
                      <Switch
                        checked={isEnabled}
                        onChange={(e) => updateRule(onToggle(e, rules))}
                      />
                      <i
                        className={classNames([
                          icon,
                          'ml-[24px]',
                          'mr-[12px]',
                          'text-xl',
                        ])}
                      ></i>
                      <Typography className='my-[8px]'>
                        {label(rules, true)}
                      </Typography>
                      {isEnabled && Editor && (
                        <Editor rules={rules} onChange={updateRule} />
                      )}
                    </Stack>
                  );
                }
              )}
            </Stack>
          </div>
          <Divider orientation='vertical' flexItem />
          <div className='w-1/2 py-[60px] px-[100px]'>
            <Typography variant='body1' sx={{ mb: '24px' }} fontWeight={600}>
              Which resources should follow these rules?
            </Typography>
            <ConditionEditor
              condition={condition}
              options={{
                supportedProperties: supportedControlProperties(tagsFiltersV2),
                allowUsingProperty: (c) =>
                  condition?.property !== c.name &&
                  !condition?.conditions?.some(
                    (condition) => condition.property === c.name
                  ),
              }}
              onChange={(condition) => updateState({ condition })}
            />
            <Divider className='my-[40px]' />
            <Stack>
              <Typography variant='body1' sx={{ mb: '24px' }} fontWeight={600}>
                What is the severity of this control?
              </Typography>
              <ControlSeverity
                severity={props.wizardState.severity}
                onChange={updateSeverity}
              />
            </Stack>
          </div>
        </Stack>
      </Stack>
    </StepContainer>
  );
};

function ControlSeverity({
  severity,
  onChange,
}: {
  severity: Severity;
  onChange: (severity: Severity) => void;
}) {
  return (
    <Select
      sx={{
        width: '150px',
        boxShadow: 'none',
        '.MuiOutlinedInput-notchedOutline': { border: 0 },
      }}
      value={severity}
      onChange={(e) => onChange(e.target.value as Severity)}
      displayEmpty
      renderValue={(value) => (
        <Box className='flex h-[22px] mr-[10px] my-auto justify-center items-center self-center'>
          <ControlIndicator
            backgroundColor={ControlsSeverity[value.toLowerCase()]?.color}
          />
          <Typography className='ml-[12px] flex-grow'>
            {ControlsSeverity[value.toLowerCase()]?.title}
          </Typography>
        </Box>
      )}
    >
      {Object.values(Severity).map((option) => (
        <MenuItem key={option} value={option}>
          <ControlIndicator
            backgroundColor={ControlsSeverity[option.toLowerCase()]?.color}
          />
          <ListItemText
            className='ml-[12px]'
            primary={ControlsSeverity[option.toLowerCase()]?.title}
          />
        </MenuItem>
      ))}
    </Select>
  );
}
